/**
 * navConfig.ts — Single source of truth for four-pillar navigation.
 *
 * Both desktop and mobile nav render from this config.
 * Feature-gated tabs hide automatically when the flag is falsy.
 *
 * Command R4: Role-specific tabs — Doms see 7 tabs and Subs see 7 tabs.
 */

import { isDomRole } from '../../utils/roleHelpers';

export type PillarId = 'cultivate' | 'connect' | 'command' | 'chronicle' | 'admin';

export interface PillarTab {
  /** Route path */
  to: string;
  /** Display label */
  label: string;
  /** Stable identifier for tab ordering (derived from route's last segment) */
  tabId?: string;
  /** Optional feature flag key — tab hidden when flag is falsy */
  featureGate?: string;
  /** Maps to TerminologyKey for dynamic labels (pluralized automatically) */
  terminologyKey?: string;
  /** When true, tab is hidden in the pillar nav */
  hideInV2?: boolean;
}

export interface PillarConfig {
  id: PillarId;
  label: string;
  subtitle: string;
  baseRoute: string;
  /** CSS custom-property name for this pillar's accent color */
  accentVar: string;
  /** Icon component name from Icons.tsx */
  icon: string;
  /** If true, only shown when user is super_admin */
  adminOnly?: boolean;
  /** First-visit tooltip content (Spec 163) */
  tooltip?: {
    title: string;
    description: string;
  };
  tabs: PillarTab[];
}

/**
 * Legacy 13-tab Command config — kept for reference; no longer shown in nav.
 * (Consequences removed in Spec 169 — now lives inside Protocols tab only.)
 */
const LEGACY_COMMAND_TABS: PillarTab[] = [
  { to: '/command', tabId: 'tasks', label: 'Tasks', terminologyKey: 'task' },
  { to: '/command/structure/training', tabId: 'training', label: 'Training', featureGate: 'commandTraining' },
  { to: '/command/structure/contracts', tabId: 'contracts', label: 'Contracts', featureGate: 'commandContracts' },
  { to: '/command/protocols', tabId: 'protocols', label: 'Protocols', featureGate: 'commandProtocols', terminologyKey: 'protocol' },
  { to: '/command/chastity', tabId: 'chastity', label: 'Chastity', featureGate: 'chastityManagement' },
  { to: '/command/progress', tabId: 'progress', label: 'Progress', featureGate: 'commandProgress' },
  { to: '/command/praise', tabId: 'praise', label: 'Praise', featureGate: 'commandPraise', terminologyKey: 'praise' },
  { to: '/command/points', tabId: 'points', label: 'Points', featureGate: 'commandPraise' },
  { to: '/command/rewards', tabId: 'rewards', label: 'Rewards', featureGate: 'commandRewards', terminologyKey: 'reward' },
  // Rotations deprecated (Spec 126) — migrated to tasks, nav entry removed
  { to: '/command/orgasm-bank', tabId: 'orgasm-bank', label: 'Orgasm Bank', featureGate: 'commandOrgasmBank' },
  { to: '/command/structure/autonomy', tabId: 'autonomy', label: 'Autonomy', featureGate: 'commandAutonomy' },
  { to: '/command/travel', tabId: 'travel', label: 'Travel', featureGate: 'commandTravel' },
  { to: '/command/reviews', tabId: 'reviews', label: 'Reviews', featureGate: 'commandReviews' },
];

/**
 * Command R4 — Dom tabs (7 tabs)
 * Economy tab is the dom's admin view of Points, Rewards, Orgasm Bank, Praise, Streaks.
 */
const DOM_COMMAND_TABS: PillarTab[] = [
  { to: '/command', tabId: 'directives', label: 'Directives', terminologyKey: 'task' },
  { to: '/command/protocols', tabId: 'protocols', label: 'Protocols', featureGate: 'commandProtocols', terminologyKey: 'protocol' },
  { to: '/command/structure', tabId: 'structure', label: 'Structure' },
  { to: '/command/economy', tabId: 'economy', label: 'Economy', terminologyKey: 'economy' },
  { to: '/command/chastity', tabId: 'chastity', label: 'Chastity', featureGate: 'chastityManagement' },
  { to: '/command/travel', tabId: 'travel', label: 'Travel', featureGate: 'commandTravel' },
  { to: '/command/reviews', tabId: 'reviews', label: 'Reviews', featureGate: 'commandReviews' },
];

/**
 * Command R4 — Sub tabs (7 tabs, Store renamed to Economy)
 */
const SUB_COMMAND_TABS: PillarTab[] = [
  { to: '/command', tabId: 'directives', label: 'Directives', terminologyKey: 'task' },
  { to: '/command/protocols', tabId: 'protocols', label: 'Protocols', featureGate: 'commandProtocols', terminologyKey: 'protocol' },
  { to: '/command/store', tabId: 'economy', label: 'Economy', featureGate: 'subStore', terminologyKey: 'economy' },
  { to: '/command/structure', tabId: 'structure', label: 'Structure' },
  { to: '/command/chastity', tabId: 'chastity', label: 'Chastity', featureGate: 'chastityManagement' },
  { to: '/command/travel', tabId: 'travel', label: 'Travel', featureGate: 'commandTravel' },
  { to: '/command/reviews', tabId: 'reviews', label: 'Reviews', featureGate: 'commandReviews' },
];

/**
 * Command R4 — Get role-specific tabs.
 *
 * @param userRole - The user's organization role ('admin'/'owner' = Dom, 'member' = Sub)
 */
export function getCommandTabs(
  userRole: 'admin' | 'member' | 'owner' | 'super_admin' | string,
): PillarTab[] {
  // 2026-07-05: previously compared userRole directly to the strings
  // "admin" and "super_admin" — the second branch checked org role against
  // a value it can never hold ('super_admin' is exclusively the platform
  // user.role); the real, previously-missed gap was the 'owner' org role.
  // See roleHelpers.ts.
  const isDom = isDomRole(userRole);
  return isDom ? DOM_COMMAND_TABS : SUB_COMMAND_TABS;
}

export const PILLAR_CONFIG: PillarConfig[] = [
  {
    id: 'cultivate',
    label: 'Cultivate',
    subtitle: 'Meet people',
    baseRoute: '/cultivate',
    accentVar: '--pillar-cultivate',
    icon: 'SeedlingIcon',
    tooltip: {
      title: 'Discover and Match',
      description: 'Browse profiles, manage your matches, and start conversations.',
    },
    tabs: [
      { to: '/cultivate', tabId: 'feed', label: 'Feed' },
      { to: '/cultivate/hierarchies', tabId: 'hierarchies', label: 'Hierarchies' },
      { to: '/cultivate/matches', tabId: 'matches', label: 'Matches' },
      { to: '/cultivate/messages', tabId: 'messages', label: 'Messages' },
      { to: '/cultivate/profile', tabId: 'profile', label: 'Profile' },
      // C1 (2026-06-07 audit): was hideInV2 with no alternative v2 entry → discovery-privacy
      // controls + delete-profile danger zone were unreachable on v2. Restored to the v2 nav.
      { to: '/cultivate/settings', tabId: 'settings', label: 'Settings' },
    ],
  },
  {
    id: 'connect',
    label: 'Connect',
    subtitle: 'Communities & social',
    baseRoute: '/connect',
    accentVar: '--pillar-connect',
    icon: 'UsersIcon',
    tooltip: {
      title: 'Community and Learning',
      description: 'Join communities, find mentors, and share content.',
    },
    tabs: [
      { to: '/connect', tabId: 'communities', label: 'Communities' },
      { to: '/connect/mentorship', tabId: 'mentorship', label: 'Mentorship', featureGate: 'mentorship' },
      { to: '/connect/messages', tabId: 'messages', label: 'Messages' },
      { to: '/connect/shared', tabId: 'shared', label: 'Directory' },
    ],
  },
  {
    id: 'command',
    label: 'Command',
    subtitle: 'Structure & authority',
    baseRoute: '/command',
    accentVar: '--pillar-command',
    icon: 'CrownIcon',
    tooltip: {
      title: 'Your Dynamic',
      description: 'Tasks, protocols, contracts, chastity, economy, and more.',
    },
    tabs: LEGACY_COMMAND_TABS,
  },
  {
    id: 'chronicle',
    label: 'Chronicle',
    subtitle: 'Know yourself',
    baseRoute: '/chronicle',
    accentVar: '--pillar-chronicle',
    icon: 'BookIcon',
    tooltip: {
      title: 'Your Story',
      description: 'Journal entries, scene logs, boundaries, and milestones.',
    },
    tabs: [
      { to: '/chronicle', tabId: 'today', label: 'Today' },
      { to: '/chronicle/journal', tabId: 'journal', label: 'Journal' },
      { to: '/chronicle/scenes', tabId: 'scenes', label: 'Scenes' },
      { to: '/chronicle/boundaries', tabId: 'boundaries', label: 'Boundaries' },
      { to: '/chronicle/milestones', tabId: 'milestones', label: 'Milestones', featureGate: 'collaringStages' },
      { to: '/chronicle/library', tabId: 'library', label: 'Library', featureGate: 'mediaLibrary' },
      { to: '/chronicle/inventory', tabId: 'inventory', label: 'Inventory', featureGate: 'inventory' },
      { to: '/chronicle/shared', tabId: 'shared', label: 'Shared', featureGate: 'unifiedSharing' },
      { to: '/chronicle/archive', tabId: 'archive', label: 'Archive', featureGate: 'dynamicEvolutionArchive' },
    ],
  },
];

/**
 * Admin pillar — shown only for super_admin users.
 * Kept separate from PILLAR_CONFIG so non-admins never see it.
 */
export const ADMIN_PILLAR: PillarConfig = {
  id: 'admin',
  label: 'Admin',
  subtitle: 'Platform administration',
  baseRoute: '/admin',
  accentVar: '--pillar-admin',
  icon: 'ShieldIcon',
  adminOnly: true,
  tabs: [
    // C3 (2026-06-07 audit): '/admin' renders SuperAdminMetricsPage, so "Metrics" is the accurate
    // label — the real admin dashboard (AdminDashboardPage) is reached via the Users tab.
    { to: '/admin', tabId: 'dashboard', label: 'Metrics' },
    { to: '/admin/users', tabId: 'users', label: 'Users' },
    { to: '/admin/organizations', tabId: 'organizations', label: 'Organizations' },
    { to: '/admin/platform-config', tabId: 'platform-config', label: 'Config' },
    // C5 (2026-06-07 audit): both pages are real + API-backed (324/352 lines) but had no nav
    // entry — reachable by direct URL only. Surfaced here next to the other config-admin tools.
    { to: '/admin/inventory-types', tabId: 'inventory-types', label: 'Item Types' },
    { to: '/admin/organization-categories', tabId: 'organization-categories', label: 'Org Categories' },
    { to: '/admin/moderation', tabId: 'moderation', label: 'Moderation' },
    { to: '/admin/ai-moderation', tabId: 'ai-moderation', label: 'AI Mod' },
    // Spec 308 — Trust Level Moderation UI. Rate-limit config for the 5
    // trust tiers; per-user trust actions live on AdminUserDetailPage.
    { to: '/admin/trust-levels', tabId: 'trust-levels', label: 'Trust & Safety' },
    { to: '/admin/audit-logs', tabId: 'audit-logs', label: 'Audit Logs' },
    { to: '/admin/auth-audit-log', tabId: 'auth-audit-log', label: 'Auth Audit' },
    { to: '/admin/storage', tabId: 'storage', label: 'Storage' },
    { to: '/admin/monitoring', tabId: 'monitoring', label: 'Monitoring' },
    { to: '/admin/invitations', tabId: 'invitations', label: 'Invitations' },
    { to: '/admin/marketing', tabId: 'marketing', label: 'Marketing' },
  ],
};

/**
 * Derive active pillar from the current pathname.
 * Returns null when on a non-pillar page (e.g. /helm, /settings).
 */
export function getActivePillar(pathname: string): PillarId | null {
  // Check admin first (more specific /admin prefix)
  if (pathname === ADMIN_PILLAR.baseRoute || pathname.startsWith(ADMIN_PILLAR.baseRoute + '/')) {
    return 'admin';
  }
  for (const pillar of PILLAR_CONFIG) {
    if (pathname === pillar.baseRoute || pathname.startsWith(pillar.baseRoute + '/')) {
      return pillar.id;
    }
  }
  return null;
}

/**
 * Get the config object for a pillar by its ID.
 */
export function getPillarConfig(id: PillarId): PillarConfig | undefined {
  if (id === 'admin') return ADMIN_PILLAR;
  return PILLAR_CONFIG.find((p) => p.id === id);
}

/**
 * Routes where detail views hide the MobileBottomNav so a fixed 64px action
 * bar can own the bottom edge (anatomy contract §3.7 / A9).
 *
 * Round 4 Batch 0 ships this EMPTY — the infrastructure lands dark and detail
 * views (task detail, community detail, Chastity manage) opt in during Batch 3.
 *
 * No `g` or `y` flags on these patterns: RegExp.test() is stateful with
 * sticky/global regexes (lastIndex persists across calls), which would make
 * shouldHideBottomNav alternate results on repeated renders. Pinned by test.
 */
export const HIDE_BOTTOM_NAV_ROUTES: RegExp[] = [
  // Task detail (/tasks/:id) — not create/import, and not deeper segments
  // like /tasks/:id/completions (those keep the nav).
  /^\/tasks\/(?!create$|import$)[^/]+$/,
  // Community detail (/communities/:communityId) — removed Spec 277 Track A
  // along with the rest of legacy /communities/*; Channels (/connect) never
  // had an equivalent entry here.
  // Chastity manage is a Sheet overlay, not a route — no entry needed.
];

export function shouldHideBottomNav(
  pathname: string,
  routes: RegExp[] = HIDE_BOTTOM_NAV_ROUTES
): boolean {
  return routes.some((re) => re.test(pathname));
}
